import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import type { Credential, Customer } from '../lib/types'

/** Welche Felder eine Zugangsart braucht. Spiegelt validateCredential im Server. */
const KINDS = {
  snmp_v2c: {
    label: 'SNMP v2c',
    fields: [{ name: 'community', label: 'Community', secret: true, required: true }],
    fixed: { version: '2c' },
  },
  snmp_v3: {
    label: 'SNMP v3',
    fields: [
      { name: 'username', label: 'Benutzername', secret: false, required: true },
      { name: 'auth_protocol', label: 'Authentifizierung (SHA256, SHA, MD5)', secret: false, required: true },
      { name: 'auth_password', label: 'Authentifizierungs-Kennwort', secret: true, required: true },
      { name: 'priv_protocol', label: 'Verschlüsselung (AES, AES256, DES)', secret: false, required: false },
      { name: 'priv_password', label: 'Verschlüsselungs-Kennwort', secret: true, required: false },
    ],
    fixed: { version: '3' },
  },
  ssh: {
    label: 'SSH',
    fields: [
      { name: 'username', label: 'Benutzername', secret: false, required: true },
      { name: 'password', label: 'Kennwort', secret: true, required: false },
      { name: 'private_key', label: 'Privater Schlüssel', secret: true, required: false },
    ],
    fixed: {},
  },
  api_token: {
    label: 'API-Token',
    fields: [{ name: 'token', label: 'Token', secret: true, required: true }],
    fixed: {},
  },
} as const

type Kind = keyof typeof KINDS

/**
 * Zugangsdaten anlegen. Die Werte gehen verschlüsselt in den Tresor und werden
 * nie wieder angezeigt – die Liste zeigt nur Name und Art.
 */
export function CredentialSection({ customers }: { customers: Customer[] }) {
  const queryClient = useQueryClient()
  const [customerId, setCustomerId] = useState('')
  const [name, setName] = useState('')
  const [kind, setKind] = useState<Kind>('snmp_v2c')
  const [fields, setFields] = useState<Record<string, string>>({})

  const credentials = useQuery({
    queryKey: ['credentials', customerId || null],
    queryFn: ({ signal }) => apiGet<Credential[]>(`/customers/${customerId}/credentials`, signal),
    enabled: Boolean(customerId),
  })

  const create = useMutation({
    mutationFn: () =>
      apiSend<Credential>('POST', `/customers/${customerId}/credentials`, {
        name: name.trim(),
        kind,
        fields: { ...KINDS[kind].fixed, ...trimmed(fields) },
      }),
    onSuccess: () => {
      setName('')
      setFields({})
      void queryClient.invalidateQueries({ queryKey: ['credentials'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/credentials/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['credentials'] }),
  })

  const spec = KINDS[kind]
  const complete =
    customerId !== '' &&
    name.trim() !== '' &&
    spec.fields.every((f) => !f.required || (fields[f.name] ?? '').trim() !== '')

  return (
    <section className="card">
      <div className="card-head">
        <h2 className="card-title">Zugangsdaten</h2>
        <span className="muted" style={{ fontSize: 12 }}>
          Verschlüsselt gespeichert, nie wieder angezeigt
        </span>
      </div>
      <form
        className="card-body stack"
        onSubmit={(e) => {
          e.preventDefault()
          if (complete) create.mutate()
        }}
      >
        <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ minWidth: 220 }}>
            <label htmlFor="zugang-kunde">Kunde</label>
            <select
              id="zugang-kunde"
              className="input"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value="">Bitte wählen</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ minWidth: 180 }}>
            <label htmlFor="zugang-name">Bezeichnung</label>
            <input
              id="zugang-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="SNMP Wien"
            />
          </div>
          <div className="field" style={{ minWidth: 160 }}>
            <label htmlFor="zugang-art">Art</label>
            <select
              id="zugang-art"
              className="input"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as Kind)
                setFields({})
              }}
            >
              {Object.entries(KINDS).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {spec.fields.map((f) => (
            <div className="field" key={f.name} style={{ minWidth: 200 }}>
              <label htmlFor={`zugang-${f.name}`}>
                {f.label}
                {f.required ? '' : ' (optional)'}
              </label>
              <input
                id={`zugang-${f.name}`}
                className="input"
                type={f.secret ? 'password' : 'text'}
                autoComplete="off"
                value={fields[f.name] ?? ''}
                onChange={(e) => setFields((prev) => ({ ...prev, [f.name]: e.target.value }))}
              />
            </div>
          ))}
          <button type="submit" className="btn btn-primary" disabled={create.isPending || !complete}>
            Zugang speichern
          </button>
          {create.isError ? (
            <span style={{ color: 'var(--crit)' }}>{(create.error as Error).message}</span>
          ) : null}
        </div>
      </form>

      {customerId && (credentials.data ?? []).length > 0 ? (
        <table className="data">
          <thead>
            <tr>
              <th>Bezeichnung</th>
              <th>Art</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(credentials.data ?? []).map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td className="muted">{KINDS[c.kind as Kind]?.label ?? c.kind}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Zugang "${c.name}" löschen? Geräte, die ihn nutzen, werden danach nicht mehr abgefragt.`)) {
                        remove.mutate(c.id)
                      }
                    }}
                  >
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  )
}

function trimmed(fields: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(fields)) {
    if (v.trim() !== '') out[k] = v.trim()
  }
  return out
}
